import { useCallback } from 'react';
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form';

import { isCentralizedWallet, isDecentralizedWallet } from '@/entities/assets/helpers/guards';
import { PrivateDocumentCategoryToText } from '@/entities/document';
import { BankCellAccessMethodsList, BankCellList } from '@/shared/lib/forms/asset-form/bank-cell';
import { BondsList } from '@/shared/lib/forms/asset-form/bonds';
import { BrokerageAccountList } from '@/shared/lib/forms/asset-form/brokerage-account';
import { CurrenciesList } from '@/shared/lib/forms/asset-form/centralized-wallet';
import { CommercialRealEstateList } from '@/shared/lib/forms/asset-form/commercial-real-estate';
import {
  BaseAssetTypesList,
  PlaceOfRecordList,
} from '@/shared/lib/forms/asset-form/commodity-asset-token';
import { DebitList } from '@/shared/lib/forms/asset-form/debit-account';
import { BasicAssetList } from '@/shared/lib/forms/asset-form/exchange-options';
import { ForwardsList } from '@/shared/lib/forms/asset-form/forwards';
import { FuturesList } from '@/shared/lib/forms/asset-form/futures';
import { JewelryList } from '@/shared/lib/forms/asset-form/jewelry';
import { MetalAccountTypeList } from '@/shared/lib/forms/asset-form/metal-account';
import { MyFundList } from '@/shared/lib/forms/asset-form/my-fund';
import { PreciousGemstonesList } from '@/shared/lib/forms/asset-form/precious-gemstones';
import { PublicFundTypesList } from '@/shared/lib/forms/asset-form/public-fund';
import { ResidentialRealEstateList } from '@/shared/lib/forms/asset-form/residential-real-estate';
import { SecuritiesAndStocksList } from '@/shared/lib/forms/asset-form/securities-and-stocks';
import { PrivateDocumentDtoType } from '@/shared/lib/forms/documents/private';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import {
  AssetCategory,
  AssetDto,
  NAME_KEY_BY_ASSET_CATEGORY,
} from '@/shared/lib/sj-orm/models/asset.dto';
import {
  BasicAssetType,
  CryptoWalletType,
  JewelryType,
  PlaceOfRecord,
  PreciousGemstonesType,
} from '@/shared/lib/sj-orm/models/assets/enums';
import {
  ContactType,
  isBankContact,
  isBrokerContact,
} from '@/shared/lib/sj-orm/models/contact.dto';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { CreateAssetParams } from '.';

export enum QuestionType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  COUNTRY = 'COUNTRY',
  SELECT = 'SELECT',
  CURRENCY = 'CURRENCY',
  OWNERSHIP = 'OWNERSHIP',
  SECTION_TITLE = 'SECTION_TITLE',
  MULTIPLE_SELECT = 'MULTIPLE_SELECT',
}

export type IWFormItemType = {
  type: QuestionType;
  label: string;
  placeholder?: string;
  required?: boolean;
  halfWidth?: boolean;
  items?: {
    label: string;
    value: string | null;
    onSelect?: (setValue: UseFormSetValue<CreateAssetParams>) => void;
    openCreateForm?: {
      formTitle: string;
      allowedCategories: AssetCategory[];
      onSubmit?: (assetDto?: AssetDto, setValue?: UseFormSetValue<CreateAssetParams>) => void;
    };
  }[];
  showWhen?: {
    field: string;
    is?: string;
    not?: string;
    isFilled?: boolean;
    check?: (getValues: UseFormGetValues<CreateAssetParams>) => boolean;
  };
};

export const useForms = (): Record<AssetCategory, Record<string, IWFormItemType>> => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'IWCreateAsset' });
  const { t: tf } = useTranslation(['forms_assets', 'contacts']);

  const sjDatabase = useSJDatabase();
  const banks = sjDatabase.collections.contacts?.findMany((item) => item.type === ContactType.BANK);
  const brokers = sjDatabase.collections.contacts?.findMany(
    (item) => item.type === ContactType.BROKER,
  );

  const privateDocuments = sjDatabase.collections.privateDocuments?.findMany(() => true) ?? [];

  const nicknameOptions = {
    label: t('nickname.label'),
    placeholder: t('nickname.placeholder'),
    required: true,
    type: QuestionType.TEXT,
  };

  const translateSelectList = (list: { label: string; key: string }[]) => {
    return list.map(({ label, key }) => ({ label: tf(label), value: key }));
  };

  const getWalletDto = (walledId?: string) => {
    if (!walledId) return false;
    const walletDto = sjDatabase.collections.assets?.findById(walledId);
    return walletDto;
  };

  const getPlaceOfRecord = useCallback(
    (collection: 'assets' | 'privateDocuments' | 'contacts'): IWFormItemType['items'] => {
      switch (collection) {
        case 'assets':
          return [
            ...(sjDatabase.collections.assets
              ?.findMany(
                (item) =>
                  item.category === AssetCategory.DECENTRALIZED_WALLET ||
                  item.category === AssetCategory.CENTRALIZED_WALLET,
              )
              .map((item) => ({
                label: String(item[NAME_KEY_BY_ASSET_CATEGORY[item.category] as keyof AssetDto]),
                value: item.id,
                onSelect: (setValue: UseFormSetValue<CreateAssetParams>) => {
                  if (isCentralizedWallet(item)) {
                    setValue('placeOfRecordCryptoHolder', item.holder);
                    setValue('placeOfRecordCryptoAccount', item.accountNumber);
                    setValue('placeOfRecordCryptoStorage', item.loginStorageLocation);
                  }
                  if (isDecentralizedWallet(item)) {
                    setValue(
                      'placeOfRecordCryptoWalletName',
                      String(item.officialWalletName ?? ''),
                    );
                    setValue(
                      'placeOfRecordCryptoDeviceLocation',
                      String(item.deviceStorageLocation ?? ''),
                    );
                    setValue('placeOfRecordCryptoStorage', String(item.loginStorageLocation ?? ''));
                  }
                },
              })) ?? []),
            {
              label: t('placeOfRecordDetails.addCryptowallet'),
              value: 'new',
              openCreateForm: {
                formTitle: t('addCryptowallet'),
                allowedCategories: [
                  AssetCategory.DECENTRALIZED_WALLET,
                  AssetCategory.CENTRALIZED_WALLET,
                ],
                onSubmit: (asset, setValue) => {
                  if (!asset || !setValue) return;

                  if (isCentralizedWallet(asset)) {
                    setValue('placeOfRecordCryptowallet', asset.id);
                    setValue('placeOfRecordCryptoWalletName', undefined);
                    setValue('placeOfRecordCryptoDeviceLocation', undefined);
                    setValue('placeOfRecordCryptoHolder', asset.holder);
                    setValue('placeOfRecordCryptoAccount', asset.accountNumber);
                    setValue('placeOfRecordCryptoStorage', asset.loginStorageLocation);
                  }
                  if (isDecentralizedWallet(asset)) {
                    setValue('placeOfRecordCryptowallet', asset.id);
                    setValue('placeOfRecordCryptoHolder', undefined);
                    setValue('placeOfRecordCryptoAccount', undefined);
                    setValue(
                      'placeOfRecordCryptoWalletName',
                      String(asset.officialWalletName ?? ''),
                    );
                    setValue(
                      'placeOfRecordCryptoDeviceLocation',
                      String(asset.deviceStorageLocation ?? ''),
                    );
                    setValue(
                      'placeOfRecordCryptoStorage',
                      String(asset.loginStorageLocation ?? ''),
                    );
                  }
                },
              },
            },
          ];

        case 'privateDocuments':
          return [
            ...(sjDatabase.collections.privateDocuments
              ?.findMany(() => true)
              .map((item) => ({
                label: item.name,
                value: item.id,
                onSelect: (setValue: UseFormSetValue<CreateAssetParams>) => {
                  setValue('placeOfRecordDocumentName', item.name);
                  setValue('placeOfRecordDocumentType', item.privateCategory);
                  setValue('placeOfRecordDocumentCountry', item.country);
                  setValue('placeOfRecordDocumentNumber', (item as PrivateDocumentDtoType).number);
                  setValue(
                    'placeOfRecordDocumentLocation',
                    (item as PrivateDocumentDtoType).storageLocation,
                  );
                },
              })) ?? []),
            {
              label: t('placeOfRecordDetails.addDocument'),
              value: 'new',
              onSelect: (setValue: UseFormSetValue<CreateAssetParams>) => {
                setValue('placeOfRecordDocumentName', '');
                setValue('placeOfRecordDocumentNumber', '');
                setValue('placeOfRecordDocumentLocation', '');
              },
            },
          ];
        case 'contacts':
          return [
            ...(sjDatabase.collections.contacts
              ?.findMany(
                (item) => item.type === ContactType.BANK || item.type === ContactType.BROKER,
              )
              .map((item) => ({
                label: item.nickname ?? '',
                value: item.id,
                onSelect: (setValue: UseFormSetValue<CreateAssetParams>) => {
                  setValue('placeOfRecordContactsType', item.type);
                  if (isBrokerContact(item)) {
                    setValue('placeOfRecordContactsBrokerName', item.nickname);
                    setValue('placeOfRecordContactsBrokerCountry', item.selectedCountry);
                    setValue('placeOfRecordContactsBrokerAddress', item.brokerAddress);
                  }
                  if (isBankContact(item)) {
                    setValue('placeOfRecordContactsBankName', item.nickname);
                    setValue('placeOfRecordContactsBankCountry', item.selectedCountry);

                    setValue('placeOfRecordContactsBankAddress', item.bankAddress);
                  }
                },
              })) ?? []),
            {
              label: t('placeOfRecordDetails.addContact'),
              value: 'new',
              onSelect: (setValue: UseFormSetValue<CreateAssetParams>) => {
                setValue('placeOfRecordContactsBankName', '');
                setValue('placeOfRecordContactsBankAddress', '');
                setValue('placeOfRecordContactsBrokerName', '');
                setValue('placeOfRecordContactsBrokerAddress', '');
              },
            },
          ];
        default:
          return undefined;
      }
    },
    [sjDatabase],
  );

  const formBaseFields: Record<string, IWFormItemType> = {
    country: {
      label: t('country.label'),
      placeholder: t('country.placeholder'),
      required: true,
      type: QuestionType.COUNTRY,
      halfWidth: true,
    },
    value: {
      label: t('value.label'),
      placeholder: t('value.placeholder'),
      required: true,
      type: QuestionType.CURRENCY,
      halfWidth: true,
    },
    ownerShipType: {
      label: t('ownershipType.label'),
      placeholder: t('ownershipType.placeholder'),
      required: true,
      type: QuestionType.OWNERSHIP,
    },
  };

  const estateFormFileds: Record<string, IWFormItemType> = {
    country: formBaseFields.country,
    value: {
      ...formBaseFields.value,
      label: t('marketValue.label'),
      placeholder: t('marketValue.placeholder'),
    },
    ownerShipType: formBaseFields.ownerShipType,
    address: {
      label: t('address.label'),
      type: QuestionType.SECTION_TITLE,
    },
    district: {
      label: t('district.label'),
      placeholder: t('district.placeholder'),
      required: true,
      type: QuestionType.TEXT,
    },
    city: {
      label: t('city.label'),
      placeholder: t('city.placeholder'),
      required: true,
      type: QuestionType.TEXT,
      halfWidth: true,
    },
    street: {
      label: t('street.label'),
      placeholder: t('street.placeholder'),
      required: true,
      type: QuestionType.TEXT,
      halfWidth: true,
    },
    buildingNumber: {
      label: t('house.label'),
      placeholder: t('house.placeholder'),
      type: QuestionType.TEXT,
      halfWidth: true,
    },
    apartments: {
      label: t('apt.label'),
      placeholder: t('apt.placeholder'),
      type: QuestionType.TEXT,
      halfWidth: true,
    },

    details: {
      label: t('details.label'),
      type: QuestionType.SECTION_TITLE,
    },
  };

  const brokerFormFields: Record<string, IWFormItemType> = {
    brokerTitle: {
      label: t('brokerTitle.label'),
      type: QuestionType.SECTION_TITLE,
    },
    holder: {
      label: t('broker.label'),
      placeholder: t('broker.placeholder'),
      type: QuestionType.SELECT,
      items: [
        {
          value: null,
          label: t('broker.none'),
        },
        ...(banks || []).map((item) => ({
          value: item.id,
          label: item.nickname,
        })),
        ...(brokers || []).map((item) => ({
          value: item.id,
          label: item.nickname,
        })),
        {
          value: 'new',
          label: t('broker.addNew'),
        },
      ],
    },
    bankName: {
      label: t('brokerName.label'),
      placeholder: t('brokerName.placeholder'),
      required: true,
      type: QuestionType.TEXT,
      showWhen: { field: 'holder', isFilled: true },
    },
    bankCounty: {
      ...formBaseFields.country,
      halfWidth: false,
      showWhen: { field: 'holder', isFilled: true },
    },
    bankAddress: {
      label: t('brokerAddress.label'),
      placeholder: t('brokerAddress.placeholder'),
      required: true,
      type: QuestionType.TEXT,
      showWhen: { field: 'holder', isFilled: true },
    },
  };

  const fundsFormFileds = {
    fullLegalName: {
      label: t('fullLegalName.label'),
      placeholder: t('fullLegalName.placeholder'),
      required: true,
      type: QuestionType.TEXT,
    },
    lei: {
      label: t('lei.label'),
      placeholder: t('lei.placeholder'),
      halfWidth: true,
      type: QuestionType.TEXT,
    },
    taxNumber: {
      label: t('taxNumber.label'),
      placeholder: t('taxNumber.placeholder'),
      halfWidth: true,
      type: QuestionType.TEXT,
    },
    legalTitle: {
      label: t('legalEntityAddress.label'),
      type: QuestionType.SECTION_TITLE,
    },
    legalEntityAddress: {
      label: t('legalEntityAddress.label'),
      placeholder: t('legalEntityAddress.placeholder'),
      type: QuestionType.SELECT,
      items: [
        {
          value: null,
          label: t('legalEntityAddress.none'),
        },
        {
          value: 'new',
          label: t('legalEntityAddress.addNew'),
        },
      ],
    },
    province: {
      label: t('district.label'),
      placeholder: t('district.placeholder'),
      required: true,
      type: QuestionType.TEXT,
      showWhen: { field: 'legalEntityAddress', is: 'new' },
    },
    city: {
      label: t('city.label'),
      placeholder: t('city.placeholder'),
      required: true,
      type: QuestionType.TEXT,
      halfWidth: true,
      showWhen: { field: 'legalEntityAddress', is: 'new' },
    },
    street: {
      label: t('street.label'),
      placeholder: t('street.placeholder'),
      required: true,
      type: QuestionType.TEXT,
      halfWidth: true,
      showWhen: { field: 'legalEntityAddress', is: 'new' },
    },
    buildingNumber: {
      label: t('house.label'),
      placeholder: t('house.placeholder'),
      type: QuestionType.TEXT,
      halfWidth: true,
      showWhen: { field: 'legalEntityAddress', is: 'new' },
    },
    apartments: {
      label: t('apt.label'),
      placeholder: t('apt.placeholder'),
      type: QuestionType.TEXT,
      halfWidth: true,
      showWhen: { field: 'legalEntityAddress', is: 'new' },
    },
  };

  return {
    [AssetCategory.RESIDENTIAL_REAL_ESTATE]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.RESIDENTIAL_REAL_ESTATE]]: nicknameOptions,
      type: {
        label: t('estateType.label'),
        placeholder: t('estateType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(ResidentialRealEstateList),
      },
      ...estateFormFileds,
      residentialRealEstateNumberName: {
        label: t('estateNumberName.label'),
        placeholder: t('estateNumberName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
      residentialRealEstateNumber: {
        label: t('estateNumber.label'),
        placeholder: t('estateNumber.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
    },
    [AssetCategory.COMMERCIAL_REAL_ESTATE]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.COMMERCIAL_REAL_ESTATE]]: nicknameOptions,
      type: {
        label: t('estateType.label'),
        placeholder: t('estateType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(CommercialRealEstateList),
      },
      ...estateFormFileds,
      apartments: {
        ...estateFormFileds.apartments,
        label: t('office.label'),
        placeholder: t('office.placeholder'),
      },
      commercialRealEstateNumberName: {
        label: t('estateNumberName.label'),
        placeholder: t('estateNumberName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
      commercialRealEstateNumber: {
        label: t('estateNumber.label'),
        placeholder: t('estateNumber.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
    },
    [AssetCategory.LAND]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.LAND]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('marketValue.label'),
        placeholder: t('marketValue.placeholder'),
      },
      ownerShipType: formBaseFields.ownerShipType,
      address: {
        label: t('address.label'),
        type: QuestionType.SECTION_TITLE,
      },
      district: {
        label: t('district.label'),
        placeholder: t('district.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        halfWidth: true,
      },
      city: {
        label: t('city.label'),
        placeholder: t('city.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        halfWidth: true,
      },
      street: {
        label: t('street.label'),
        placeholder: t('street.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        halfWidth: true,
      },
      buildingNumber: {
        label: t('plot.label'),
        placeholder: t('plot.placeholder'),
        type: QuestionType.TEXT,
        halfWidth: true,
      },

      details: {
        label: t('details.label'),
        type: QuestionType.SECTION_TITLE,
      },

      numberName: {
        label: t('plotNumberName.label'),
        placeholder: t('plotNumberName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
      number: {
        label: t('plotNumberNumber.label'),
        placeholder: t('plotNumberNumber.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
    },
    [AssetCategory.DEBIT_ACCOUNTS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.DEBIT_ACCOUNTS]]: nicknameOptions,
      type: {
        label: t('accountType.label'),
        placeholder: t('accountType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(DebitList),
      },
      ...formBaseFields,
      iban: {
        label: t('accountNumberIban.label'),
        placeholder: t('accountNumberIban.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
      banklabel: {
        label: t('bank.label'),
        type: QuestionType.SECTION_TITLE,
      },
      bank: {
        label: t('bank.label'),
        placeholder: t('bank.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: [
          ...(banks || []).map((item) => ({
            value: item.id,
            label: item.nickname,
          })),
          {
            value: 'new',
            label: t('bank.addNew'),
          },
        ],
      },
      bankName: {
        label: t('bankName.label'),
        placeholder: t('bankName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'bank', isFilled: true },
      },
      bankCounty: {
        label: t('bankCounty.label'),
        placeholder: t('bankCounty.placeholder'),
        required: true,
        type: QuestionType.COUNTRY,
        showWhen: { field: 'bank', isFilled: true },
      },
      bankAddress: {
        label: t('bankAddress.label'),
        placeholder: t('bankAddress.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'bank', isFilled: true },
      },
    },
    [AssetCategory.METAL_ACCOUNTS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.METAL_ACCOUNTS]]: nicknameOptions,
      type: {
        type: QuestionType.SELECT,
        label: t('metalType.label'),
        placeholder: t('metalType.placeholder'),
        items: translateSelectList(MetalAccountTypeList),
        required: true,
      },
      country: {
        label: t('country.label'),
        placeholder: t('country.placeholder'),
        required: true,
        type: QuestionType.COUNTRY,
        halfWidth: true,
      },
      value: {
        label: t('marketValue.label'),
        placeholder: t('marketValue.placeholder'),
        required: true,
        type: QuestionType.CURRENCY,
        halfWidth: true,
      },
      ownerShipType: {
        label: t('ownershipType.label'),
        placeholder: t('ownershipType.placeholder'),
        required: true,
        type: QuestionType.OWNERSHIP,
        halfWidth: true,
      },
      iban: {
        label: t('accountNumberIban.label'),
        placeholder: t('accountNumberIban.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        halfWidth: true,
      },
      banklabel: {
        label: t('bank.label'),
        type: QuestionType.SECTION_TITLE,
      },
      holder: {
        label: t('bank.label'),
        placeholder: t('bank.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: [
          ...(banks || []).map((item) => ({
            value: item.id,
            label: item.nickname,
          })),
          {
            value: 'new',
            label: t('bank.addNew'),
          },
        ],
      },
      bankName: {
        label: t('bankName.label'),
        placeholder: t('bankName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'holder', isFilled: true },
      },
      bankCounty: {
        label: t('bankCounty.label'),
        placeholder: t('bankCounty.placeholder'),
        required: true,
        type: QuestionType.COUNTRY,
        showWhen: { field: 'holder', isFilled: true },
      },
      bankAddress: {
        label: t('bankAddress.label'),
        placeholder: t('bankAddress.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'holder', isFilled: true },
      },
    },
    [AssetCategory.BANK_CELLS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.BANK_CELLS]]: nicknameOptions,
      type: {
        type: QuestionType.SELECT,
        label: t('depositBoxType.label'),
        placeholder: t('depositBoxType.placeholder'),
        items: translateSelectList(BankCellList),
        required: true,
      },
      country: {
        label: t('country.label'),
        placeholder: t('country.placeholder'),
        required: true,
        type: QuestionType.COUNTRY,
        halfWidth: true,
      },
      value: {
        label: t('totalValue.label'),
        placeholder: t('totalValue.placeholder'),
        required: true,
        type: QuestionType.CURRENCY,
        halfWidth: true,
      },
      ownerShipType: {
        label: t('ownershipType.label'),
        placeholder: t('ownershipType.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.OWNERSHIP,
      },
      depositBoxNumber: {
        label: t('depositBoxNumber.label'),
        placeholder: t('depositBoxNumber.placeholder'),
        type: QuestionType.TEXT,
        halfWidth: true,
        required: true,
      },
      accessMethod: {
        type: QuestionType.SELECT,
        label: t('accessMethod.label'),
        placeholder: t('accessMethod.placeholder'),
        items: translateSelectList(BankCellAccessMethodsList),
        required: true,
      },
      banklabel: {
        label: t('bank.label'),
        type: QuestionType.SECTION_TITLE,
      },
      bank: {
        label: t('bank.label'),
        placeholder: t('bank.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: [
          ...(banks || []).map((item) => ({
            value: item.id,
            label: item.nickname,
          })),
          {
            value: 'new',
            label: t('bank.addNew'),
          },
        ],
      },
      bankName: {
        label: t('bankName.label'),
        placeholder: t('bankName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'bank', isFilled: true },
      },
      bankCounty: {
        label: t('bankCounty.label'),
        placeholder: t('bankCounty.placeholder'),
        required: true,
        type: QuestionType.COUNTRY,
        showWhen: { field: 'bank', isFilled: true },
      },
      bankAddress: {
        label: t('bankAddress.label'),
        placeholder: t('bankAddress.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'bank', isFilled: true },
      },
    },
    [AssetCategory.BROKERAGE_ACCOUNT]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.BROKERAGE_ACCOUNT]]: nicknameOptions,
      type: {
        label: t('brokerageType.label'),
        placeholder: t('brokerageType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(BrokerageAccountList),
      },
      country: { ...formBaseFields.country },
      value: {
        ...formBaseFields.value,
        label: t('totalBalance.label'),
        placeholder: t('totalBalance.placeholder'),
      },
      ownerShipType: {
        label: t('ownershipType.label'),
        placeholder: t('ownershipType.placeholder'),
        required: true,
        type: QuestionType.OWNERSHIP,
      },
      iban: {
        label: t('accountNumber.label'),
        placeholder: t('accountNumber.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
      brokerTitle: brokerFormFields.brokerTitle,
      holder: {
        ...brokerFormFields.holder,
        required: true,
      },
      bankName: brokerFormFields.bankName,
      bankCounty: brokerFormFields.bankCounty,
      bankAddress: brokerFormFields.bankAddress,
    },
    [AssetCategory.SECURITIES_AND_STOCKS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.SECURITIES_AND_STOCKS]]: nicknameOptions,
      type: {
        label: t('stocksType.label'),
        placeholder: t('stocksType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(SecuritiesAndStocksList),
      },
      country: { ...formBaseFields.country },
      value: {
        ...formBaseFields.value,
        label: t('valuePerStock.label'),
        placeholder: t('valuePerStock.placeholder'),
      },
      ownerShipType: {
        label: t('ownershipType.label'),
        placeholder: t('ownershipType.placeholder'),
        required: true,
        type: QuestionType.OWNERSHIP,
      },
      isin: {
        label: t('isin.label'),
        placeholder: t('isin.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      brokerageAccountNumber: {
        label: t('brokerAccountNumber.label'),
        placeholder: t('brokerAccountNumber.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      depositaryName: {
        label: t('depositaryName.label'),
        placeholder: t('depositaryName.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      depositaryAccountNumber: {
        label: t('depositaryNumber.label'),
        placeholder: t('depositaryNumber.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      ...brokerFormFields,
    },
    [AssetCategory.BONDS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.BONDS]]: nicknameOptions,
      type: {
        label: t('bondsType.label'),
        placeholder: t('bondsType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(BondsList),
      },
      country: { ...formBaseFields.country },
      value: {
        ...formBaseFields.value,
        label: t('valuePerBond.label'),
        placeholder: t('valuePerBond.placeholder'),
      },
      ownerShipType: {
        label: t('ownershipType.label'),
        placeholder: t('ownershipType.placeholder'),
        required: true,
        type: QuestionType.OWNERSHIP,
      },
      isin: {
        label: t('isin.label'),
        placeholder: t('isin.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      brokerageAccountNumber: {
        label: t('accountNumber.label'),
        placeholder: t('accountNumber.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      depositaryName: {
        label: t('depositaryName.label'),
        placeholder: t('depositaryName.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      amount: {
        label: t('amount.label'),
        placeholder: t('amount.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      depositaryAccountNumber: {
        label: t('depositaryNumber.label'),
        placeholder: t('depositaryNumber.placeholder'),
        type: QuestionType.TEXT,
      },
      ...brokerFormFields,
    },
    [AssetCategory.FUTURES]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.FUTURES]]: nicknameOptions,
      type: {
        label: t('futuresType.label'),
        placeholder: t('futuresType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(FuturesList),
      },
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('valuePerFutures.label'),
        placeholder: t('valuePerFutures.placeholder'),
      },
      ownerShipType: formBaseFields.ownerShipType,

      ticker: {
        label: t('ticker.label'),
        placeholder: t('ticker.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
      basicAssetName: {
        label: t('basicName.label'),
        placeholder: t('basicName.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      expirationDate: {
        label: t('expirationDate.label'),
        placeholder: t('expirationDate.placeholder'),
        halfWidth: true,
        type: QuestionType.DATE,
      },
      amount: {
        label: t('amount.label'),
        placeholder: t('amount.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      brokerAccountNumber: {
        label: t('accountNumber.label'),
        placeholder: t('accountNumber.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      broker: {
        label: t('broker.label'),
        placeholder: t('broker.placeholder'),
        type: QuestionType.SELECT,
        items: [
          {
            value: null,
            label: t('broker.none'),
          },
          ...(brokers || []).map((item) => ({
            value: item.id,
            label: item.nickname,
          })),
          {
            value: 'new',
            label: t('broker.addNew'),
          },
        ],
      },
      bankName: {
        label: t('brokerName.label'),
        placeholder: t('brokerName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'broker', isFilled: true },
      },
      bankCounty: {
        ...formBaseFields.country,
        halfWidth: false,
        showWhen: { field: 'broker', isFilled: true },
      },
      bankAddress: {
        label: t('brokerAddress.label'),
        placeholder: t('brokerAddress.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'broker', isFilled: true },
      },
    },
    [AssetCategory.EXCHANGE_OPTIONS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.EXCHANGE_OPTIONS]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('valuePerOption.label'),
        placeholder: t('valuePerOption.placeholder'),
      },
      ownerShipType: formBaseFields.ownerShipType,
      type: {
        label: t('optionType.label'),
        placeholder: t('optionType.placeholder'),
        type: QuestionType.SELECT,
        required: true,
        items: [
          {
            label: 'None',
            value: null,
          },
          {
            label: 'Call option',
            value: 'call',
          },
          {
            label: 'Put option',
            value: 'put',
          },
        ],
      },
      ticker: {
        label: t('ticker.label'),
        placeholder: t('ticker.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      amount: {
        label: t('amount.label'),
        placeholder: t('amount.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },

      expirationDate: {
        label: t('expirationDate.label'),
        placeholder: t('expirationDate.placeholder'),
        halfWidth: true,
        type: QuestionType.DATE,
      },
      brokerAccountNumber: {
        label: t('accountNumber.label'),
        placeholder: t('accountNumber.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      broker: {
        label: t('broker.label'),
        placeholder: t('broker.placeholder'),
        type: QuestionType.SELECT,
        items: [
          {
            value: null,
            label: t('broker.none'),
          },
          ...(brokers || []).map((item) => ({
            value: item.id,
            label: item.nickname,
          })),
          {
            value: 'new',
            label: t('broker.addNew'),
          },
        ],
      },
      bankName: {
        label: t('brokerName.label'),
        placeholder: t('brokerName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'broker', isFilled: true },
      },
      bankCounty: {
        ...formBaseFields.country,
        halfWidth: false,
        showWhen: { field: 'broker', isFilled: true },
      },
      bankAddress: {
        label: t('brokerAddress.label'),
        placeholder: t('brokerAddress.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        showWhen: { field: 'broker', isFilled: true },
      },
    },
    [AssetCategory.PUBLIC_FUND]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.PUBLIC_FUND]]: nicknameOptions,
      type: {
        label: t('fundType.label'),
        placeholder: t('fundType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(PublicFundTypesList),
      },
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('valueInvestment.label'),
        placeholder: t('valueInvestment.placeholder'),
      },
      ownerShipType: { ...formBaseFields.ownerShipType, halfWidth: true },

      ...fundsFormFileds,
      fullLegalName: {
        label: t('fullLegalName.label'),
        placeholder: t('fullLegalName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
        halfWidth: true,
      },
    },
    [AssetCategory.MY_FUND]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.MY_FUND]]: nicknameOptions,
      type: {
        label: t('fundType.label'),
        placeholder: t('fundType.placeholder'),
        required: true,
        type: QuestionType.SELECT,
        items: translateSelectList(MyFundList),
      },
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('valueFund.label'),
        placeholder: t('valueFund.placeholder'),
      },
      ownerShipType: formBaseFields.ownerShipType,

      ...fundsFormFileds,
    },
    [AssetCategory.LEGAL_ENTITIES]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.LEGAL_ENTITIES]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('capitalization.label'),
        placeholder: t('capitalization.placeholder'),
      },
      ownerShipType: formBaseFields.ownerShipType,
      legalName: {
        label: t('fullLegalName.label'),
        placeholder: t('fullLegalName.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
      structure: {
        label: t('lei.label'),
        placeholder: t('lei.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      taxNumber: {
        label: t('taxNumber.label'),
        placeholder: t('taxNumber.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      dateOfIncorporation: {
        label: t('dateOfIncorporation.label'),
        placeholder: t('dateOfIncorporation.placeholder'),
        halfWidth: true,
        type: QuestionType.DATE,
      },
      province: {
        label: t('district.label'),
        placeholder: t('district.placeholder'),
        type: QuestionType.TEXT,
        halfWidth: true,
      },
      city: {
        label: t('city.label'),
        placeholder: t('city.placeholder'),
        type: QuestionType.TEXT,
        halfWidth: true,
      },
      street: {
        label: t('street.label'),
        placeholder: t('street.placeholder'),
        type: QuestionType.TEXT,
        halfWidth: true,
      },
      buildingNumber: {
        label: t('house.label'),
        placeholder: t('house.placeholder'),
        type: QuestionType.TEXT,
        halfWidth: true,
      },
      apartments: {
        label: t('apt.label'),
        placeholder: t('apt.placeholder'),
        type: QuestionType.TEXT,
        halfWidth: true,
      },
    },
    [AssetCategory.OVER_THE_COUNTER_OPTIONS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.OVER_THE_COUNTER_OPTIONS]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('valuePerOption.label'),
        placeholder: t('valuePerOption.placeholder'),
      },
      ownerShipType: formBaseFields.ownerShipType,

      type: {
        label: t('optionType.label'),
        placeholder: t('optionType.placeholder'),
        type: QuestionType.SELECT,
        required: true,
        halfWidth: true,
        items: [
          {
            label: 'Call option',
            value: 'call',
          },
          {
            label: 'Put option',
            value: 'put',
          },
        ],
      },
      amount: {
        label: t('amount.label'),
        placeholder: t('amount.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      basicAssetName: {
        label: t('basicName.label'),
        placeholder: t('basicName.placeholder'),
        type: QuestionType.TEXT,
        required: true,
      },
      basicAssetHolder: {
        label: t('basicAssetHolder.label'),
        placeholder: t('basicAssetHolder.placeholder'),
        type: QuestionType.TEXT,
        required: true,
        halfWidth: true,
      },
      counterpartyType: {
        label: t('counterpartyType.label'),
        placeholder: t('counterpartyType.placeholder'),
        type: QuestionType.SELECT,
        items: [
          {
            value: 'person',
            label: t('counterpartyType.person'),
          },
          {
            value: 'legalEntity',
            label: t('counterpartyType.legal'),
          },
        ],
        halfWidth: true,
        required: true,
      },
      legalEntityName: {
        label: t('legalEntityName.label'),
        placeholder: t('legalEntityName.placeholder'),
        type: QuestionType.TEXT,
        required: true,
        showWhen: { field: 'counterpartyType', is: 'legalEntity' },
      },
      personFullName: {
        label: t('personFullName.label'),
        placeholder: t('personFullName.placeholder'),
        type: QuestionType.TEXT,
        required: true,
        showWhen: { field: 'counterpartyType', is: 'person' },
      },
    },
    [AssetCategory.FORWARDS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.FORWARDS]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('valuePerForward.label'),
        placeholder: t('valuePerForward.placeholder'),
      },
      ownerShipType: {
        ...formBaseFields.ownerShipType,
        halfWidth: true,
      },
      type: {
        label: t('forwardType.label'),
        placeholder: t('forwardType.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.SELECT,
        items: translateSelectList(ForwardsList),
      },
      basicAssetType: {
        label: t('underlyingAssetType.label'),
        placeholder: t('underlyingAssetType.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.SELECT,
        items: BasicAssetList.filter((n) => n.key !== BasicAssetType.stocks).map((item) => ({
          value: item.key,
          label: tf(item.label),
        })),
      },
      counterpartyType: {
        label: t('counterpartyType.label'),
        placeholder: t('counterpartyType.placeholder'),
        type: QuestionType.SELECT,
        items: [
          {
            value: 'person',
            label: t('counterpartyType.person'),
          },
          {
            value: 'legalEntity',
            label: t('counterpartyType.legal'),
          },
        ],
        halfWidth: true,
        required: true,
      },
      legalEntityName: {
        label: t('legalEntityName.label'),
        placeholder: t('legalEntityName.placeholder'),
        type: QuestionType.TEXT,
        required: true,
        showWhen: { field: 'counterpartyType', is: 'legalEntity' },
      },
      personFullName: {
        label: t('personFullName.label'),
        placeholder: t('personFullName.placeholder'),
        type: QuestionType.TEXT,
        required: true,
        showWhen: { field: 'counterpartyType', is: 'person' },
      },
      amount: {
        label: t('amount.label'),
        placeholder: t('amount.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },
    },
    [AssetCategory.CENTRALIZED_WALLET]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.CENTRALIZED_WALLET]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('totalValue.label'),
        placeholder: t('totalValue.placeholder'),
      },
      ownerShipType: {
        ...formBaseFields.ownerShipType,
        halfWidth: true,
      },

      accountNumber: {
        label: t('accountNumber.label'),
        placeholder: t('accountNumber.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      holder: {
        label: t('holder.label'),
        placeholder: t('holder.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      loginStorageLocation: {
        label: t('loginStorageLocation.label'),
        placeholder: t('loginStorageLocation.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
    },
    [AssetCategory.DECENTRALIZED_WALLET]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.DECENTRALIZED_WALLET]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('totalValue.label'),
        placeholder: t('totalValue.placeholder'),
      },
      ownerShipType: formBaseFields.ownerShipType,

      assetsInWallet: {
        label: t('assetsInWallet.label'),
        placeholder: t('assetsInWallet.placeholder'),
        required: true,
        type: QuestionType.MULTIPLE_SELECT,
        items: CurrenciesList.map((n) => ({
          label: tf(n.label),
          value: n.key,
        })),
      },
      walletType: {
        label: t('walletType.label'),
        placeholder: t('walletType.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.SELECT,
        items: [
          {
            label: t('walletType.cold'),
            value: CryptoWalletType.cold,
          },
          {
            label: t('walletType.hot'),
            value: CryptoWalletType.hot,
          },
        ],
      },
      officialWalletName: {
        label: t('officialWalletName.label'),
        placeholder: t('officialWalletName.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      mnemonicStorageLocation: {
        label: t('mnemonicStorageLocation.label'),
        placeholder: t('mnemonicStorageLocation.placeholder'),
        required: true,
        type: QuestionType.TEXT,
      },

      deviceStorageLocation: {
        label: t('deviceStorageLocation.label'),
        placeholder: t('deviceStorageLocation.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      loginStorageLocation: {
        label: t('loginStorageLocation.label'),
        placeholder: t('loginStorageLocation.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
    },
    [AssetCategory.JEWELLERY]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.JEWELLERY]]: nicknameOptions,
      ...formBaseFields,
      ownerShipType: { ...formBaseFields.ownerShipType, halfWidth: true },
      type: {
        label: t('jewelryType.label'),
        placeholder: t('jewelryType.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.SELECT,
        items: JewelryList.map((n) => ({
          label: tf(n.label),
          value: n.key,
        })),
      },
      otherType: {
        type: QuestionType.TEXT,
        label: t('otherJewelryType.label'),
        placeholder: t('otherJewelryType.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'type',
          is: JewelryType.other,
        },
      },
      serialNumber: {
        label: t('serialNumber.label'),
        placeholder: t('serialNumber.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      storageLocation: {
        label: t('storageLocation.label'),
        placeholder: t('storageLocation.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },

      receipt: {
        type: QuestionType.SELECT,
        items: [
          ...(privateDocuments ?? []).map((item) => ({
            label: item.name,
            value: item.id,
          })),
          {
            value: 'new',
            label: t('receipt.addNew'),
          },
        ],
        required: true,
        label: t('receipt.label'),
        placeholder: t('receipt.placeholder'),
      },

      receiptDocType: {
        label: t('documentType.label'),
        placeholder: t('documentType.placeholder'),
        type: QuestionType.SELECT,
        required: true,
        items: Object.entries(PrivateDocumentCategoryToText).map(([key, value]) => ({
          label: tf(value),
          value: key,
        })),
        showWhen: {
          field: 'receipt',
          is: 'new',
        },
      },
      receiptDocName: {
        label: t('documentName.label'),
        placeholder: t('documentName.placeholder'),
        type: QuestionType.TEXT,
        required: true,
        showWhen: {
          field: 'receipt',
          is: 'new',
        },
      },
      receiptDocCountry: {
        label: t('country.label'),
        placeholder: t('country.placeholder'),
        required: true,
        type: QuestionType.COUNTRY,
        showWhen: {
          field: 'receipt',
          is: 'new',
        },
      },
      receiptDocNumber: {
        label: t('documentNumber.label'),
        placeholder: t('documentNumber.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
        showWhen: {
          field: 'receipt',
          isFilled: true,
        },
      },
      receiptDocDate: {
        label: t('issueDate.label'),
        placeholder: t('issueDate.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.DATE,
        showWhen: {
          field: 'receipt',
          isFilled: true,
        },
      },
    },
    [AssetCategory.PRECIOUS_STONES]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.PRECIOUS_STONES]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('personalValue.label'),
        placeholder: t('personalValue.placeholder'),
      },
      ownerShipType: { ...formBaseFields.ownerShipType, halfWidth: true },

      type: {
        label: t('gemstoneType.label'),
        placeholder: t('gemstoneType.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.SELECT,
        items: PreciousGemstonesList.map((n) => ({
          label: tf(n.label),
          value: n.key,
        })),
      },
      otherType: {
        type: QuestionType.TEXT,
        label: t('otherGemType.label'),
        placeholder: t('otherGemType.placeholder'),
        required: true,
        showWhen: {
          field: 'type',
          is: PreciousGemstonesType.other,
        },
      },
      serialNumber: {
        label: t('serialNumber.label'),
        placeholder: t('serialNumber.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      weight: {
        label: t('caratWeight.label'),
        placeholder: t('caratWeight.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      typeOfOrigin: {
        label: t('originType.label'),
        placeholder: t('originType.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      storageLocation: {
        label: t('storageLocation.label'),
        placeholder: t('storageLocation.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      purchaseReceipt: {
        type: QuestionType.SELECT,
        items: [
          {
            value: null,
            label: t('broker.none'),
          },
          ...(privateDocuments ?? []).map((item) => ({
            label: item.name,
            value: item.id,
          })),
          {
            value: 'new',
            label: t('receipt.addNew'),
          },
        ],
        required: false,
        label: t('receipt.label'),
        placeholder: t('receipt.placeholder'),
      },

      receiptDocType: {
        label: t('documentType.label'),
        placeholder: t('documentType.placeholder'),
        type: QuestionType.SELECT,
        required: true,
        items: Object.entries(PrivateDocumentCategoryToText).map(([key, value]) => ({
          label: tf(value),
          value: key,
        })),
        showWhen: {
          field: 'purchaseReceipt',
          is: 'new',
        },
      },
      receiptDocName: {
        label: t('documentName.label'),
        placeholder: t('documentName.placeholder'),
        type: QuestionType.TEXT,
        required: true,
        showWhen: {
          field: 'purchaseReceipt',
          is: 'new',
        },
      },
      receiptDocCountry: {
        label: t('country.label'),
        placeholder: t('country.placeholder'),
        required: true,
        type: QuestionType.COUNTRY,
        showWhen: {
          field: 'purchaseReceipt',
          is: 'new',
        },
      },
      receiptDocNumber: {
        label: t('documentNumber.label'),
        placeholder: t('documentNumber.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.TEXT,
        showWhen: {
          field: 'purchaseReceipt',
          isFilled: true,
        },
      },
      receiptDocDate: {
        label: t('issueDate.label'),
        placeholder: t('issueDate.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.DATE,
        showWhen: {
          field: 'purchaseReceipt',
          isFilled: true,
        },
      },
    },
    [AssetCategory.COMMODITY_ASSET_TOKENS]: {
      [NAME_KEY_BY_ASSET_CATEGORY[AssetCategory.COMMODITY_ASSET_TOKENS]]: nicknameOptions,
      country: formBaseFields.country,
      value: {
        ...formBaseFields.value,
        label: t('valuePerUnit.label'),
        placeholder: t('valuePerUnit.placeholder'),
      },
      ownerShipType: formBaseFields.ownerShipType,

      identifierName: {
        label: t('identifierName.label'),
        placeholder: t('identifierName.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
        required: true,
      },
      identifierNumber: {
        label: t('identifierNumber.label'),
        placeholder: t('identifierNumber.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
        required: true,
      },
      baseAssetType: {
        label: t('underlyingAssetType.label'),
        placeholder: t('underlyingAssetType.placeholder'),
        required: true,
        halfWidth: true,
        type: QuestionType.SELECT,
        items: BaseAssetTypesList.map((item) => ({
          value: item.key,
          label: tf(item.label),
        })),
      },
      issuerLegalName: {
        label: t('issuerLegalName.label'),
        placeholder: t('issuerLegalName.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
        required: true,
      },
      amount: {
        label: t('amount.label'),
        placeholder: t('amount.placeholder'),
        halfWidth: true,
        required: true,
        type: QuestionType.TEXT,
      },
      issuerLocation: {
        label: t('issuerLocation.label'),
        placeholder: t('issuerLocation.placeholder'),
        halfWidth: true,
        type: QuestionType.TEXT,
      },
      placeOfRecordTitle: {
        type: QuestionType.SECTION_TITLE,
        label: t('placeOfRecord.label'),
      },
      placeOfRecord: {
        type: QuestionType.SELECT,
        label: t('placeOfRecord.label'),
        placeholder: t('placeOfRecord.placeholder'),
        required: true,
        items: translateSelectList(PlaceOfRecordList),
      },
      placeOfRecordCryptowallet: {
        type: QuestionType.SELECT,
        label: t('placeOfRecordDetails.label'),
        placeholder: t('placeOfRecordDetails.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecord',
          is: PlaceOfRecord.cryptowallet,
        },
        items: getPlaceOfRecord('assets'),
      },
      placeOfRecordDocument: {
        type: QuestionType.SELECT,
        label: t('placeOfRecordDetails.label'),
        placeholder: t('placeOfRecordDetails.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecord',
          is: PlaceOfRecord.document,
        },
        items: getPlaceOfRecord('privateDocuments'),
      },
      placeOfRecordFinancialInstitution: {
        type: QuestionType.SELECT,
        label: t('placeOfRecordDetails.label'),
        placeholder: t('placeOfRecordDetails.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecord',
          is: PlaceOfRecord.financialInstitution,
        },
        items: getPlaceOfRecord('contacts'),
      },
      placeOfRecordOther: {
        type: QuestionType.TEXT,
        label: t('placeOfRecordDetails.label'),
        placeholder: t('placeOfRecordDetails.placeholderText'),
        required: true,
        showWhen: {
          field: 'placeOfRecord',
          is: PlaceOfRecord.other,
        },
      },
      //  crypto
      placeOfRecordCryptoHolder: {
        type: QuestionType.TEXT,
        label: t('holder.label'),
        placeholder: t('holder.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordCryptowallet',
          isFilled: true,
          not: 'new',
          check: (getValues) => {
            const wallet = getWalletDto(getValues('placeOfRecordCryptowallet'));
            if (!wallet) return false;
            return isCentralizedWallet(wallet);
          },
        },
      },
      placeOfRecordCryptoAccount: {
        type: QuestionType.TEXT,
        label: t('accountNumber.label'),
        placeholder: t('accountNumber.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordCryptowallet',
          isFilled: true,
          not: 'new',
          check: (getValues) => {
            const wallet = getWalletDto(getValues('placeOfRecordCryptowallet'));
            if (!wallet) return false;
            return isCentralizedWallet(wallet);
          },
        },
      },

      placeOfRecordCryptoWalletName: {
        type: QuestionType.TEXT,
        label: t('officialWalletName.label'),
        placeholder: t('officialWalletName.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordCryptowallet',
          isFilled: true,
          not: 'new',
          check: (getValues) => {
            const wallet = getWalletDto(getValues('placeOfRecordCryptowallet'));
            if (!wallet) return false;
            return isDecentralizedWallet(wallet);
          },
        },
      },

      placeOfRecordCryptoDeviceLocation: {
        type: QuestionType.TEXT,
        label: t('deviceStorageLocation.label'),
        placeholder: t('deviceStorageLocation.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordCryptowallet',
          isFilled: true,
          not: 'new',
          check: (getValues) => {
            const wallet = getWalletDto(getValues('placeOfRecordCryptowallet'));

            if (!wallet) return false;
            return isDecentralizedWallet(wallet);
          },
        },
      },

      placeOfRecordCryptoStorage: {
        type: QuestionType.TEXT,
        label: t('storageLocation.label'),
        placeholder: t('storageLocation.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecordCryptowallet',
          isFilled: true,
          not: 'new',
        },
      },
      // contacts
      placeOfRecordContactsType: {
        type: QuestionType.SELECT,
        label: t('contactType.label'),
        placeholder: t('contactType.placeholder'),
        required: true,
        items: [
          {
            label: tf('contacts:contactType.bank'),
            value: ContactType.BANK,
          },
          {
            label: tf('contacts:contactType.broker'),
            value: ContactType.BROKER,
          },
        ],
        showWhen: {
          field: 'placeOfRecordFinancialInstitution',
          isFilled: true,
        },
      },
      placeOfRecordContactsBankName: {
        type: QuestionType.TEXT,
        label: t('bankName.label'),
        placeholder: t('bankName.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordContactsType',
          is: ContactType.BANK,
        },
      },
      placeOfRecordContactsBankCountry: {
        type: QuestionType.COUNTRY,
        label: t('contactCountry.label'),
        placeholder: t('contactCountry.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordContactsType',
          is: ContactType.BANK,
        },
      },
      placeOfRecordContactsBankAddress: {
        type: QuestionType.TEXT,
        label: t('bankAddress.label'),
        placeholder: t('bankAddress.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecordContactsType',
          is: ContactType.BANK,
        },
      },
      placeOfRecordContactsBrokerName: {
        type: QuestionType.TEXT,
        label: t('brokerName.label'),
        placeholder: t('brokerName.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordContactsType',
          is: ContactType.BROKER,
        },
      },
      placeOfRecordContactsBrokerCountry: {
        type: QuestionType.COUNTRY,
        label: t('contactCountry.label'),
        placeholder: t('contactCountry.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordContactsType',
          is: ContactType.BROKER,
        },
      },
      placeOfRecordContactsBrokerAddress: {
        type: QuestionType.TEXT,
        label: t('brokerAddress.label'),
        placeholder: t('brokerAddress.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecordContactsType',
          is: ContactType.BROKER,
        },
      },
      // document
      placeOfRecordDocumentType: {
        type: QuestionType.SELECT,
        label: t('documentType.label'),
        placeholder: t('documentType.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecordDocument',
          isFilled: true,
        },
        items: Object.entries(PrivateDocumentCategoryToText).map(([key, value]) => ({
          label: tf(value),
          value: key,
        })),
      },
      placeOfRecordDocumentName: {
        type: QuestionType.TEXT,
        label: t('documentName.label'),
        placeholder: t('documentName.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecordDocument',
          is: 'new',
        },
      },
      placeOfRecordDocumentCountry: {
        type: QuestionType.COUNTRY,
        label: t('country.label'),
        placeholder: t('country.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordDocument',
          isFilled: true,
        },
      },
      placeOfRecordDocumentNumber: {
        type: QuestionType.TEXT,
        label: t('documentNumber.label'),
        placeholder: t('documentNumber.placeholder'),
        required: true,
        halfWidth: true,
        showWhen: {
          field: 'placeOfRecordDocument',
          isFilled: true,
        },
      },
      placeOfRecordDocumentLocation: {
        type: QuestionType.TEXT,
        label: t('storageLocation.label'),
        placeholder: t('storageLocation.placeholder'),
        required: true,
        showWhen: {
          field: 'placeOfRecordDocument',
          isFilled: true,
        },
      },
    },
  };
};
